import { useMemo } from 'react';
import type { ImageInfo, UploadResponse } from 'graphql-ts-types';

import { FieldChangeTrigger } from 'common/enums/form';
import fetch from 'common/fetch';
import { executeQuery } from 'common/gql';
import type { ImagesProps } from '../components/Fields/Images';
import generateUploadUrlMutation from '../gql/mutation/generateUploadUrl.gql';

export const IMAGE_VALUE_SEPARATOR = '|';

export const valueToImages = (value?: string) =>
  value && value.length > 0 ? value.split(IMAGE_VALUE_SEPARATOR) : [];

const imagesToValue = (images?: string[]) => images?.join(IMAGE_VALUE_SEPARATOR) ?? '';

const generatePreSignedUrls = async (files: File[]) => {
  const imagesInfo: ImageInfo[] = files.map(({ name, size, type }) => ({
    name,
    size,
    mimetype: type,
  }));

  const {
    uploadUrl: { uploads },
  } = await executeQuery<{ uploadUrl: UploadResponse }>({
    query: generateUploadUrlMutation,
    variables: { imagesInfo },
    useApq: true,
  });

  return uploads;
};

const removeQueryParamsFromURL = (url: string) => {
  const urlObject = new URL(url);
  urlObject.search = '';

  return urlObject.toString();
};

export const useImages = ({ onChange, value, name: fieldName }: ImagesProps) => {
  const images = useMemo(() => valueToImages(value), [value]);

  const deleteImage = (src: string) => {
    onChange?.({
      fieldName,
      value: imagesToValue(images?.filter(pic => pic !== src)),
      trigger: FieldChangeTrigger.Change,
    });

    return Promise.resolve();
  };

  const uploadImage = async (files: File[]) => {
    const uploads = await generatePreSignedUrls(files);

    await Promise.all(
      uploads.map((upload, index) => fetch(upload.url, { method: 'PUT', body: files[index] }))
    );

    onChange?.({
      fieldName,
      trigger: FieldChangeTrigger.Change,
      value: imagesToValue([
        ...(images ?? []),
        ...uploads.map(({ url }) => removeQueryParamsFromURL(url)),
      ]),
    });
  };

  return {
    images,
    deleteImage,
    uploadImage,
  };
};
